"use client"

// components/tracker/digest-next/wheel-gradient.tsx
//
// The wheel card's live gradient (FE-DIGEST-1 Phase D3b2, round 2). One
// GrainGradient renderer for the card, inside the wheel stage so it follows
// the zone shift with no extra code.
//
// PLACEMENT. The host takes each Paper layer's own box (get_jsx, read
// 2026-10-02): 594x434 with transform-origin 0 0, translate 680px 557px and
// its printed rotation (2OBL-0 company 270.01deg, 2OH8-0 readout 269.96deg,
// 2OH9-0 research 270deg, 2OHA-0 indication 270.25deg), and for Trial change
// 2OBM-0, 731x555 at translate 562px 626px, rotate 269.65deg. The layer's
// white backing and its three 20px corners come with it.
//
// CHANGE OF TYPE. The palette and the colour count change in place through
// setUniforms (Trial change has four colours, the others five) and the host
// moves to the new box. Before that, a still copy of the outgoing canvas
// (drawImage from the public canvasElement, with the drawing buffer
// preserved) is placed exactly where the outgoing host was and fades from 1
// to 0 over 300ms above the live canvas. When no copy can be taken, the
// outgoing type's still JPG fades instead. A new change during a fade
// replaces the copy, never stacks. Nothing fades under reduced motion.
//
// FALLBACK. The D3b1 still JPG for the selected type is always beneath the
// canvas: it shows while the shader loads, when WebGL2 is unavailable, and
// after a context loss. The canvas shows only once it has drawn. All of it is
// aria-hidden. Disposed on unmount, on context loss and on pagehide.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react"

import type { WheelTypeId } from "@/lib/digest/wheel-groups"
import { mountGrainGradient, type GrainMount, type GrainParams } from "@/components/tracker/digest-next/grain-shader"
import styles from "@/components/tracker/digest-next/digest-next.module.css"

/** Paper's layers, as printed by get_jsx on 2OBL-0, 2OBM-0, 2OH8-0, 2OH9-0
 *  and 2OHA-0. */
const LAYERS: Record<
  WheelTypeId,
  { colors: readonly string[]; width: number; height: number; x: number; y: number; rotate: number }
> = {
  company: { colors: ["#8DD9E5", "#C55362", "#82A9B7", "#1A202E", "#1A202E"], width: 594, height: 434, x: 680, y: 557, rotate: 270.01 },
  trial: { colors: ["#A9C4C4", "#004466", "#1E3441", "#142030"], width: 731, height: 555, x: 562, y: 626, rotate: 269.65 },
  readout: { colors: ["#DDDDDD", "#929292", "#5C5C5C", "#212121", "#141417"], width: 594, height: 434, x: 680, y: 557, rotate: 269.96 },
  research: { colors: ["#F66490", "#D5527C", "#934363", "#58324A", "#1F2E3D"], width: 594, height: 434, x: 680, y: 557, rotate: 270 },
  indication: { colors: ["#DDDDDD", "#442A5E", "#2F124B", "#140B29", "#140B29"], width: 594, height: 434, x: 680, y: 557, rotate: 270.25 },
}

/** Shared by all five Paper layers. */
const SHARED = {
  frame: 27320.790999900364,
  speed: 0,
  scale: 1,
  rotation: 0,
  offsetX: 0,
  offsetY: 0,
  softness: 0.5,
  intensity: 0.51,
  noise: 0,
  shape: "wave" as const,
  colorBack: "#00000000",
  minPixelRatio: 2,
  maxPixelCount: 2_400_000,
}

/** The crossfade of the outgoing gradient: the prototype's 300ms for now
 *  (the house artwork rule says 600ms, Oliver to decide), with the house
 *  ease. One line each. */
const FADE_MS = 300
const FADE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)"

const STILL_CLASS: Record<WheelTypeId, string> = {
  research: styles.dnWheelGradientResearch,
  readout: styles.dnWheelGradientReadout,
  indication: styles.dnWheelGradientIndication,
  company: styles.dnWheelGradientCompany,
  trial: styles.dnWheelGradientTrial,
}

function paramsFor(type: WheelTypeId): GrainParams {
  return {
    colors: LAYERS[type].colors,
    colorBack: SHARED.colorBack,
    softness: SHARED.softness,
    intensity: SHARED.intensity,
    noise: SHARED.noise,
    shape: SHARED.shape,
    scale: SHARED.scale,
    rotation: SHARED.rotation,
    offsetX: SHARED.offsetX,
    offsetY: SHARED.offsetY,
  }
}

/** The host's box for a type: Paper's size, translate and rotation. */
function boxFor(type: WheelTypeId): CSSProperties {
  const l = LAYERS[type]
  return {
    width: l.width,
    height: l.height,
    transform: `translate(${l.x}px, ${l.y}px) rotate(${l.rotate}deg)`,
  }
}

type ShaderState = "pending" | "native" | "fallback"

export function WheelGradient({ type, reduced }: { type: WheelTypeId; reduced: boolean }) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const overlayHostRef = useRef<HTMLDivElement | null>(null)
  const grain = useRef<GrainMount | null>(null)
  const overlay = useRef<{ el: HTMLElement; anim: Animation | null } | null>(null)
  const shown = useRef<WheelTypeId>(type)
  const stateRef = useRef<ShaderState>("pending")
  const [state, setState] = useState<ShaderState>("pending")

  const setShaderState = (s: ShaderState) => {
    stateRef.current = s
    setState(s)
  }

  const removeOverlay = () => {
    overlay.current?.anim?.cancel()
    overlay.current?.el.remove()
    overlay.current = null
  }

  // One renderer for the card's life. Mounted with the opening type.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const g = mountGrainGradient(host, paramsFor(shown.current), {
      frame: SHARED.frame,
      speed: SHARED.speed,
      minPixelRatio: SHARED.minPixelRatio,
      maxPixelCount: SHARED.maxPixelCount,
      preserveDrawingBuffer: true,
      onReady: () => setShaderState("native"),
      onFail: () => setShaderState("fallback"),
      onLost: () => {
        removeOverlay()
        setShaderState("fallback")
      },
    })
    grain.current = g
    const onPageHide = () => {
      removeOverlay()
      g.release()
      setShaderState("fallback")
    }
    window.addEventListener("pagehide", onPageHide)
    return () => {
      window.removeEventListener("pagehide", onPageHide)
      removeOverlay()
      g.release()
      grain.current = null
    }
  }, [])

  // A type change: still copy of the outgoing gradient, then the palette in
  // place. In a layout effect, so the copy is taken before the renderer's
  // ResizeObserver redraws the canvas at the new box.
  useLayoutEffect(() => {
    const prev = shown.current
    if (prev === type) return
    shown.current = type
    removeOverlay()
    const host = overlayHostRef.current
    if (!reduced && host && typeof host.animate === "function") {
      let el: HTMLElement | null = null
      const canvas = grain.current?.mount?.canvasElement
      if (stateRef.current === "native" && canvas && canvas.width > 0 && canvas.height > 0) {
        try {
          const copy = document.createElement("canvas")
          copy.width = canvas.width
          copy.height = canvas.height
          const ctx = copy.getContext("2d")
          if (ctx) {
            ctx.drawImage(canvas, 0, 0)
            copy.className = styles.dnWheelShaderCopy
            const l = LAYERS[prev]
            copy.style.width = `${l.width}px`
            copy.style.height = `${l.height}px`
            copy.style.transform = `translate(${l.x}px, ${l.y}px) rotate(${l.rotate}deg)`
            el = copy
          }
        } catch {
          el = null
        }
      }
      if (!el) {
        const still = document.createElement("span")
        still.className = `${styles.dnWheelGradient} ${STILL_CLASS[prev]}`
        el = still
      }
      el.setAttribute("aria-hidden", "true")
      host.appendChild(el)
      const anim = el.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: FADE_MS,
        easing: FADE_EASE,
        fill: "forwards",
      })
      const entry = { el, anim }
      overlay.current = entry
      anim.finished.then(
        () => {
          if (overlay.current === entry) removeOverlay()
        },
        () => undefined,
      )
    }
    grain.current?.setUniforms(paramsFor(type))
  }, [type, reduced])

  useEffect(() => {
    if (reduced) removeOverlay()
  }, [reduced])

  return (
    <div className={styles.dnWheelGradientLayer} aria-hidden="true">
      <span className={`${styles.dnWheelGradient} ${STILL_CLASS[type]}`} />
      <div
        ref={hostRef}
        className={`${styles.dnWheelShaderHost} ${state === "native" ? styles.dnWheelShaderReady : ""}`}
        style={boxFor(type)}
        data-wheel-shader={state}
        data-wheel-type={type}
      />
      {/* The outgoing copy's place. Empty to React. */}
      <div ref={overlayHostRef} className={styles.dnWheelShaderOverlays} />
    </div>
  )
}
